import { ZyNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { type Editor } from '@tiptap/core';
import { type ReactNodeViewProps, NodeViewContent, useEditorState } from '@tiptap/react';
import { memo } from 'react';

/**
 * Checks if the multiple choice question has a correct answer. Also defaults to true if the position is not a number or the document size is not available.
 * @param editor - The editor instance
 * @param getPos - The function to get the position of the node
 * @returns True if the multiple choice question has a correct answer, false otherwise
 */
function multipleChoiceHasCorrectAnswer(editor: Editor, getPos: () => number | undefined): boolean {
    try {
        const pos = getPos();
        const docSize = editor.state?.doc?.content?.size;
        if (typeof pos !== 'number' || docSize == null || pos > docSize) {
            return true;
        }
        const parent = editor.state.doc.resolve(pos).parent;
        if (parent.type.name !== 'multipleChoice') {
            return true;
        }
        let found = false;
        parent.forEach(child => {
            if (child.type.name === 'multipleChoiceChoice' && child.attrs.isCorrect) {
                found = true;
            }
        });
        return found;
    } catch {
        return true;
    }
}

const MultipleChoiceQuestionNodeView: React.FC<ReactNodeViewProps> = ({ node, editor, getPos, decorations }) => {
    const hasCorrectAnswer = useEditorState({
        editor,
        selector: ({ editor: ed }) => (ed ? multipleChoiceHasCorrectAnswer(ed, getPos) : true),
    });

    const showError = !hasCorrectAnswer;
    const errorId = `mc-no-correct-answer-${node.attrs.id ?? getPos()}`;

    return (
        <ZyNodeViewWrapper decorations={decorations}>
            <div className="mb-2">
                <NodeViewContent />
                <span
                    id={errorId}
                    className={`text-xs text-red-600 dark:text-red-400${showError ? '' : ' hidden'}`}
                    aria-hidden={!showError}
                >
                    Mark one answer option as correct
                </span>
            </div>
        </ZyNodeViewWrapper>
    );
};

export default memo(MultipleChoiceQuestionNodeView);
